import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { question: "", answer: "" };

const FAQCRUD = () => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editFaq, setEditFaq] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 15, total: 0, total_pages: 1 });

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=faq&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setFaqs(res.data.data.map((i) => ({ id: i.id, question: i.data.question || "", answer: i.data.answer || "" })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch FAQs"); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setForm(emptyForm); setEditFaq(null); setDialogOpen(true); };
  const openEdit = (faq) => { setForm({ question: faq.question, answer: faq.answer }); setEditFaq(faq); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) { toast.warning("Please fill all fields"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("question", form.question); fd.append("answer", form.answer);
      if (editFaq) {
        await axios.put(`${BASE_URI}/entities/${editFaq.id}?category=faq`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } });
        toast.success("FAQ updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=faq`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } });
        toast.success("FAQ created!");
      }
      setDialogOpen(false); fetchFaqs(pagination.page);
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const deleteFaq = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=faq`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchFaqs(faqs.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const filtered = faqs.filter(f => f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader
        title="FAQ Management"
        description="Manage frequently asked questions displayed on the Contact page"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add FAQ</Button>}
      />
      <InfoBanner
        title="FAQ Management"
        description="Create and manage questions and answers to help visitors find information quickly."
        capabilities={["Paginated FAQ list", "Edit inline via modal", "Instant delete with confirmation"]}
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search by question or answer…" count={filtered.length} />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[35%]">Question</TableHead>
              <TableHead>Answer</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-12 text-gray-400">No FAQs yet. Add your first one.</TableCell></TableRow>
            ) : filtered.map((faq) => (
              <TableRow key={faq.id}>
                <TableCell className="font-medium text-gray-900 align-top">{faq.question}</TableCell>
                <TableCell className="text-gray-600 text-sm align-top max-w-[400px]">
                  <p className="line-clamp-3">{faq.answer}</p>
                </TableCell>
                <TableCell className="text-right align-top">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(faq)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(faq.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchFaqs(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchFaqs(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchFaqs(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete FAQ?"
        description="This action cannot be undone."
        onConfirm={deleteFaq}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editFaq ? "Edit FAQ" : "Add New FAQ"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="space-y-1.5">
              <Label>Question</Label>
              <Input placeholder="Enter question…" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Answer</Label>
              <Textarea placeholder="Enter answer…" rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} required />
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? "Saving…" : editFaq ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQCRUD;
